// Marketing funnel: where a prospective partner first came from, and one visit count per browser session.
// Nothing personal is sent: campaign tags, the referring site's host name and the page path only.

const ATTRIBUTION_KEY = "cybo_attribution";
const SESSION_KEY = "cybo_visit_counted";
const KEEP_DAYS = 90;

type Attribution = {
  source: string; medium: string; campaign: string; referrer: string; landing: string; firstSeen: string;
};

function readAttribution(): Attribution | null {
  try {
    const saved = JSON.parse(window.localStorage.getItem(ATTRIBUTION_KEY) || "null") as Attribution | null;
    if (!saved?.firstSeen) return null;
    const ageDays = (Date.now() - new Date(`${saved.firstSeen}T00:00:00`).getTime()) / 86_400_000;
    return ageDays <= KEEP_DAYS ? saved : null;
  } catch {
    return null;
  }
}

function referrerHost(): string {
  try {
    if (!document.referrer) return "";
    const host = new URL(document.referrer).hostname.toLowerCase();
    return host === window.location.hostname ? "" : host;
  } catch {
    return "";
  }
}

function localDay(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

/** Remember the first source (a tagged or referred visit replaces a plain direct one) and count this session once. */
export function recordVisit(pathname: string): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const current: Attribution = {
    source: params.get("utm_source") || "", medium: params.get("utm_medium") || "", campaign: params.get("utm_campaign") || "",
    referrer: referrerHost(), landing: pathname, firstSeen: localDay(),
  };
  const saved = readAttribution();
  const savedIsDirect = saved && !saved.source && !saved.referrer;
  if (!saved || (savedIsDirect && (current.source || current.referrer))) {
    try { window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(current)); } catch { /* storage blocked: not remembered */ }
  }
  try {
    if (window.sessionStorage.getItem(SESSION_KEY)) return;
    window.sessionStorage.setItem(SESSION_KEY, "1");
  } catch {
    return; // without session storage every page would count as a visit, so count none
  }
  void fetch("/api/v1/public/visits", {
    method: "POST", keepalive: true, headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ path: pathname, utm_source: current.source, utm_medium: current.medium, utm_campaign: current.campaign, referrer_host: current.referrer }),
  }).catch(() => undefined);
}

/** Fields sent with a partner application so the funnel can credit its first source. */
export function attributionFields(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const saved = readAttribution();
  if (!saved) return {};
  return {
    attribution_source: saved.source, attribution_medium: saved.medium, attribution_campaign: saved.campaign,
    attribution_referrer: saved.referrer, attribution_landing: saved.landing, attribution_first_seen: saved.firstSeen,
  };
}
